/**
 * Data display component overrides: avatars, badges, chips, ratings, tables.
 * @see https://mui.com/material-ui/components/avatars/
 * @see https://mui.com/material-ui/components/badges/
 * @see https://mui.com/material-ui/components/chips/
 * @see https://mui.com/material-ui/components/rating/
 * @see https://mui.com/material-ui/components/tables/
 */
import varnishTokens from '@allenai/varnish-theme/mui';
import type { Components, Theme } from '@mui/material';

export const dataDisplayComponents: Components<Omit<Theme, 'components'>> = {
    MuiAvatar: {
        styleOverrides: {
            root: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    width: '2rem',
                    height: '2rem',
                    fontSize: varnishTokens.font.size.md.value,
                    cursor: 'pointer',
                    backgroundColor: palette.default.main,
                    color: palette.default.contrastText,
                };
            },
            rounded: {
                borderRadius: varnishTokens.radii.sm.value,
            },
        },
        variants: [
            {
                props: { size: 'small' },
                // we don't have a font size this small in the theme, consider adding xs? .75rem
                style: { width: '1.5rem', height: '1.5rem', fontSize: '0.75rem' },
            },
            {
                props: { size: 'large' },
                style: {
                    width: '2.25rem',
                    height: '2.25rem',
                    fontSize: varnishTokens.font.size.lg.value,
                },
            },
            {
                props: { shape: 'square' },
                style: { borderRadius: varnishTokens.radii.sm.value },
            },
            {
                props: { shape: 'circle' },
                style: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadiusFull,
                }),
            },
            {
                props: { color: 'primary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        backgroundColor: palette.primary.main,
                        color: palette.primary.contrastText,
                    };
                },
            },
            {
                props: { color: 'secondary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        backgroundColor: palette.secondary.main,
                        color: palette.secondary.contrastText,
                    };
                },
            },
            {
                props: { color: 'tertiary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        backgroundColor: palette.tertiary.main,
                        color: palette.tertiary.contrastText,
                    };
                },
            },
            {
                props: { color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        backgroundColor: palette.default.main,
                        color: palette.default.contrastText,
                    };
                },
            },
        ],
    },
    MuiBadge: {
        styleOverrides: {
            root: { textTransform: 'none' },
        },
        variants: [
            {
                props: { color: 'tertiary' },
                style: ({ theme }) => ({
                    span: { color: (theme.vars ?? theme).palette.tertiary.main },
                }),
            },
        ],
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: varnishTokens.radii.full.value,
                // Transparent border on all chips so filled and outlined share identical dimensions
                border: '1px solid transparent',
                '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                },
            },
            labelSmall: { padding: '0 13px' },
            labelMedium: { padding: '0 15px' },
            deleteIcon: { fontSize: varnishTokens.sizes.icon.sm.value },
            sizeSmall: {
                height: '2rem',
                fontSize: varnishTokens.font.size.sm.value,
            },
            sizeMedium: {
                height: '2.25rem',
                fontSize: varnishTokens.font.size.sm.value,
            },
        },
        variants: [
            {
                props: { variant: 'filled', color: 'tertiary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        backgroundColor: palette.tertiary.main,
                        color: palette.tertiary.contrastText,
                        '&:hover': { backgroundColor: palette.tertiary.hovered?.fill },
                    };
                },
            },
            {
                props: { variant: 'outlined', color: 'tertiary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.tertiary.main,
                        borderColor: palette.tertiary.main,
                        '&:hover': { borderColor: palette.tertiary.hovered?.stroke },
                    };
                },
            },
            {
                props: { variant: 'filled', color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        backgroundColor: palette.default.main,
                        color: palette.default.contrastText,
                        '&:hover': { backgroundColor: palette.default.hovered?.fill },
                    };
                },
            },
            {
                props: { variant: 'outlined', color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.default.main,
                        borderColor: palette.default.main,
                        '&:hover': { borderColor: palette.default.hovered?.stroke },
                    };
                },
            },
            {
                props: { size: 'xsmall' },
                style: {
                    height: '1.5rem',
                    fontSize: '12px', // we don't have a font size this small in the theme, consider adding xs? .75rem
                    '& .MuiChip-label': { padding: '0 7px' },
                    '& .MuiChip-deleteIcon': { fontSize: varnishTokens.sizes.icon.xs.value },
                },
            },
            {
                props: { size: 'large' },
                style: {
                    height: '3rem',
                    fontSize: varnishTokens.font.size.md.value,
                    '& .MuiChip-label': { padding: '0 21px' },
                    '& .MuiChip-deleteIcon': { fontSize: varnishTokens.sizes.icon.lg.value },
                },
            },
            {
                props: { shape: 'box' },
                style: { borderRadius: varnishTokens.radii.sm.value },
            },
        ],
    },
    MuiRating: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: (theme.vars ?? theme).palette.primary.main,
            }),
        },
    },
    MuiTableCell: {
        styleOverrides: {
            root: { fontSize: varnishTokens.font.size.sm.value },
        },
    },
    MuiTableContainer: {
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: varnishTokens.radii.sm.value,
                backgroundColor: (theme.vars ?? theme).palette.background.default,
            }),
        },
    },
};
